import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Trophy, ArrowLeft, CheckCircle2, Loader2, Award } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export interface HtmlCourseProgressState {
  visitedPages: number[];
  currentPage: number;
  totalPages: number;
  maxPageReached: number;
  scrollProgress: number;
  answersSubmitted: boolean;
  progressPercent: number;
}

interface HtmlCoursePlayerProps {
  courseTitle: string;
  courseId: string;
  rawHtml: string;
  onComplete: () => void;
  isCompleting?: boolean;
  isAlreadyCompleted?: boolean;
  /** Resume state from a previous session */
  initialVisitedPages?: number[];
  initialCurrentPage?: number;
  initialMaxPageReached?: number;
  initialScrollProgress?: number;
  initialAnswersSubmitted?: boolean;
  /** Called (debounced) when progress changes so the parent can persist it */
  onProgressChange?: (state: HtmlCourseProgressState) => void;
  /** When true (after successful completion), shows the post-complete success overlay */
  justCompleted?: boolean;
  /** Where to send the user to view their certificate */
  onViewCertificate?: () => void;
}

/**
 * Full-screen iframe player for HTML-imported courses.
 * Tracks page/slide progress and gates completion behind visiting ALL pages.
 * Users can only advance one page at a time (sequential). Internal links are allowed
 * but the user can always navigate back to their source page.
 */
export function HtmlCoursePlayer({
  courseTitle,
  courseId,
  rawHtml,
  onComplete,
  isCompleting = false,
  isAlreadyCompleted = false,
  initialVisitedPages,
  initialCurrentPage,
  initialMaxPageReached,
  initialScrollProgress,
  initialAnswersSubmitted,
  onProgressChange,
  justCompleted = false,
  onViewCertificate,
}: HtmlCoursePlayerProps) {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const exitPath = Capacitor.isNativePlatform() ? "/app/courses" : "/learning/courses";

  const seedVisited = (() => {
    if (isAlreadyCompleted) return new Set<number>();
    if (initialVisitedPages && initialVisitedPages.length > 0) return new Set(initialVisitedPages);
    return new Set<number>([1]);
  })();
  const seedMax = isAlreadyCompleted
    ? Infinity
    : Math.max(1, initialMaxPageReached ?? 1);
  const seedCurrent = isAlreadyCompleted
    ? 1
    : Math.max(1, initialCurrentPage ?? (seedMax === Infinity ? 1 : seedMax));

  // Slide/page tracking state
  const [currentPage, setCurrentPage] = useState(seedCurrent);
  const [totalPages, setTotalPages] = useState(0);
  const [maxPageReached, setMaxPageReached] = useState<number>(seedMax);
  const [visitedPages, setVisitedPages] = useState<Set<number>>(seedVisited);
  const [hasSubmitButtons, setHasSubmitButtons] = useState(false);
  const [answersSubmitted, setAnswersSubmitted] = useState(
    isAlreadyCompleted || initialAnswersSubmitted === true
  );

  // For scroll-only content (no slide counter)
  const [scrollProgress, setScrollProgress] = useState(
    isAlreadyCompleted ? 100 : initialScrollProgress ?? 0
  );
  const [isSlideMode, setIsSlideMode] = useState(false);

  // Gate: user must actually interact with the content before it can be
  // marked visited/complete. Prevents single-container HTML uploads from
  // jumping straight to 100% the instant the iframe loads.
  const [hasInteracted, setHasInteracted] = useState(isAlreadyCompleted);
  const [exitDialogOpen, setExitDialogOpen] = useState(false);

  // Use the greater of visitedPages.size and maxPageReached so a single missed
  // page-info message (fast-click, resume, or mutation-observer race) can't
  // leave the % pinned at 99. Sequential navigation guarantees reaching page N
  // means every page up to N has been seen — same rule the completion gate uses.
  const effectiveVisited = Math.max(visitedPages.size, maxPageReached);
  const progress = isAlreadyCompleted
    ? 100
    : isSlideMode && totalPages > 0
    ? Math.min(100, Math.round((effectiveVisited / totalPages) * 100))
    : scrollProgress;
  // Sequential navigation is enforced via `maxAllowed`, so reaching the final
  // slide proves every prior slide was traversed — accept either an explicit
  // per-slide visit count OR max-page-reached hitting the total. This prevents
  // users getting stuck one slide short when a single page-info message is
  // missed (fast clicks, resume, mutation-observer race).
  const allPagesVisited =
    isAlreadyCompleted ||
    (hasInteracted &&
      isSlideMode &&
      totalPages > 0 &&
      (visitedPages.size >= totalPages || maxPageReached >= totalPages)) ||
    (hasInteracted && !isSlideMode && scrollProgress >= 95);

  // Inject Lurneax color overrides + tracking script
  const enhancedHtml = useCallback(() => {
    // Force Lurneax Clay Peach palette on all HTML content
    const colorOverrides = `
<style>
  /* ── Lurneax Clay Peach Color Overrides ── */
  :root {
    --lurneax-bg: #FDF0E8;
    --lurneax-card: #FFF8F2;
    --lurneax-primary: #F44321;
    --lurneax-ink: #2A1810;
    --lurneax-muted: #8A6050;
    --lurneax-sand: #F5E6D8;
    --lurneax-cream: #FFF8F2;
  }

  html, body {
    background-color: var(--lurneax-bg) !important;
    color: var(--lurneax-ink) !important;
    font-family: 'Nunito', sans-serif !important;
  }

  /* Override headings */
  h1, h2, h3, h4, h5, h6 {
    color: var(--lurneax-ink) !important;
    font-family: 'Fraunces', serif !important;
  }

  /* Override text elements */
  p, li, td, th, span, div, label, a {
    color: var(--lurneax-ink) !important;
  }

  /* Muted/secondary text */
  .subtitle, .description, .caption, .text-muted, .text-secondary,
  small, figcaption, blockquote {
    color: var(--lurneax-muted) !important;
  }

  /* Cards, panels, containers */
  .card, .panel, .box, .container, .content, .slide,
  section, article, main, aside, header, footer, nav,
  [class*="card"], [class*="panel"], [class*="box"],
  [class*="slide"], [class*="content"] {
    background-color: var(--lurneax-card) !important;
    border-color: var(--lurneax-sand) !important;
  }

  /* Primary accent elements — buttons only. Do NOT match [class*="primary"]
     or [class*="btn"]: many courses use classes like primary-content or
     content-btn-wrap on slide wrappers, which would paint an entire slide
     solid orange and hide all text under white-on-orange. */
  .btn, .button, button, a.cta, .cta {
    background-color: var(--lurneax-primary) !important;
    color: #FFFFFF !important;
    border-color: var(--lurneax-primary) !important;
  }

  /* Links */
  a {
    color: var(--lurneax-primary) !important;
  }
  a.cta, a.btn, a.button {
    color: #FFFFFF !important;
  }

  /* Inputs, badges, tags */
  input, select, textarea, [class*="badge"], [class*="tag"], [class*="chip"] {
    background-color: var(--lurneax-sand) !important;
    border-color: var(--lurneax-sand) !important;
    color: var(--lurneax-ink) !important;
  }

  /* Tables */
  table, tr, td, th {
    border-color: var(--lurneax-sand) !important;
  }
  th {
    background-color: var(--lurneax-sand) !important;
  }

  /* Progress bars & highlights — narrowly scoped. .active / .current /
     .selected and [class*="active"] are commonly applied to the visible slide
     itself; painting them orange with white text turns the slide into a blank
     orange panel and hides the course's own Next/Complete controls. */
  .progress-bar, .progress-fill,
  [class*="progress-bar"], [class*="progress-fill"],
  mark, .highlight {
    background-color: var(--lurneax-primary) !important;
    color: #FFFFFF !important;
  }

  /* Navigation / pagination — restrict to obvious nav wrappers only. */
  nav, .pagination, .page-indicator, .breadcrumbs {
    background-color: var(--lurneax-card) !important;
    border-color: var(--lurneax-sand) !important;
  }

  /* Code blocks */
  pre, code {
    background-color: var(--lurneax-sand) !important;
    color: var(--lurneax-ink) !important;
    font-family: 'DM Mono', monospace !important;
  }

  /* Scrollbar theming */
  ::-webkit-scrollbar { width: 8px; }
  ::-webkit-scrollbar-track { background: var(--lurneax-bg); }
  ::-webkit-scrollbar-thumb { background: var(--lurneax-sand); border-radius: 4px; }
  ::-webkit-scrollbar-thumb:hover { background: var(--lurneax-muted); }

  /* ── Hide in-HTML completion/navigation bars ── */
  /* Hide buttons that say Complete, Finish, Submit (course-level actions handled by Lurneax).
     NOTE: do NOT blanket-hide bottom-nav / footer-nav — those containers often
     also hold the course own Prev/Next buttons, and hiding them leaves users
     with no way to advance slides. Per-button hiding is handled by
     hideCompletionButtons() in the tracking script below. */
  [class*="finish-course"], [class*="course-complete"],
  [class*="completion-bar"] {
    display: none !important;
  }

  /* ── Hide embedded slide chrome (brand header, slide counter, progress bar) ──
     The Lurneax shell already shows a single DB-backed progress indicator.
     The inner HTML often ships its own top bar with brand + "X / Y" counter
     and a progress bar; suppress those to avoid duplicate indicators. */
  [class*="slide-header"], [class*="slideHeader"],
  [class*="slide-topbar"], [class*="slide-top-bar"],
  [class*="slide-progress"], [class*="slideProgress"],
  [class*="slide-counter"], [class*="slideCounter"],
  [class*="slide-nav"]:not([class*="slide-nav-button"]),
  [class*="brand-bar"], [class*="brandBar"],
  [id*="slide-progress"], [id*="slideProgress"],
  [id*="slide-counter"], [id*="slideCounter"],
  [data-slide-header], [data-slide-progress], [data-slide-counter] {
    display: none !important;
  }

  /* Import Lurneax fonts */
  @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700&family=Fraunces:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
</style>`;

    const debugFlag = (typeof window !== 'undefined' && /[?&]htmlDebug=1\b/.test(window.location.search)) ? 'true' : 'false';
    const trackingScript = `
<script>
(function() {
  var DEBUG = ${debugFlag};
  function dbg() { if (DEBUG) { try { console.log.apply(console, ['[HTMLPlayer/iframe]'].concat([].slice.call(arguments))); } catch(e){} } }
  var lastProgress = 0;
  var lastPage = 0;
  var lastTotal = 0;

  function sendProgress(progress) {
    if (progress !== lastProgress && progress >= 0) {
      lastProgress = progress;
      window.parent.postMessage({ type: 'lurneax-scroll-progress', progress: progress }, '*');
    }
  }

  function sendPageInfo(current, total) {
    if (current !== lastPage || total !== lastTotal) {
      dbg('sendPageInfo', { current: current, total: total, prevPage: lastPage, prevTotal: lastTotal });
      lastPage = current;
      lastTotal = total;
      window.parent.postMessage({ type: 'lurneax-page-info', current: current, total: total }, '*');
    }
  }

  // --- Scroll-based tracking ---
  function getScrollProgress() {
    var scrollTop = window.scrollY || document.documentElement.scrollTop;
    var scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (scrollHeight > 50) {
      return Math.min(100, Math.round((scrollTop / scrollHeight) * 100));
    }
    return -1;
  }

  // --- Slide/page detection ---
  // Lurneax owns the counter. We ignore any "N / M" text baked into a course
  // (footers, score widgets, etc.) and derive the true slide count from the
  // DOM structure so the same rule works across every HTML course and the
  // resume-progress data we persist in the DB is trustworthy.
  var SLIDE_SELECTORS = [
    '[data-slide]',
    '[data-slide-index]',
    '[data-slide-num]',
    'section.slide',
    'div.slide',
    '.slide-container > .slide',
    '.slides > section',
    '.reveal .slides > section',
    '.swiper-slide',
    '.carousel-item',
    '[role="tabpanel"].slide',
  ];
  var lockedContainer = null;
  var lockedSlides = [];
  var lockedTotal = 0;

  var attrObserver = null;
  function attachAttrObserver(slides) {
    // Watch class + attribute changes on each slide element directly. Subtree
    // observation on document.body sometimes misses attribute-only toggles on
    // descendant sections (HR/WHS templates that swap is-active on
    // section.slide), leaving the counter stuck at 1/N.
    if (attrObserver) attrObserver.disconnect();
    attrObserver = new MutationObserver(function() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(checkProgress, 60);
    });
    for (var i = 0; i < slides.length; i++) {
      try {
        attrObserver.observe(slides[i], {
          attributes: true,
          attributeFilter: ['class', 'aria-current', 'aria-hidden', 'style', 'hidden'],
        });
      } catch (e) {}
    }
  }

  function findSlideSet() {
    // Prefer the exact previously-locked slide nodes. Do not replace them with
    // every direct child of their container: HR/WHS courses place headers,
    // navigation, menus and scripts beside the 20 real slides, which previously
    // inflated the detected total to 27 and changed the meaning of each index.
    if (lockedContainer && document.contains(lockedContainer)) {
      var liveSlides = lockedSlides.filter(function(node) {
        return node && document.contains(node) && node.parentElement === lockedContainer;
      });
      if (liveSlides.length >= 2) {
        lockedSlides = liveSlides;
        lockedTotal = liveSlides.length;
        return { container: lockedContainer, slides: liveSlides };
      }
    }
    var groups = new Map();
    for (var s = 0; s < SLIDE_SELECTORS.length; s++) {
      var nodes = document.querySelectorAll(SLIDE_SELECTORS[s]);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var parent = n.parentElement;
        if (!parent) continue;
        if (!groups.has(parent)) groups.set(parent, []);
        var arr = groups.get(parent);
        if (arr.indexOf(n) === -1) arr.push(n);
      }
    }
    var bestParent = null;
    var bestSlides = [];
    groups.forEach(function(arr, parent) {
      if (arr.length >= 2 && arr.length > bestSlides.length) {
        bestParent = parent;
        bestSlides = arr;
      }
    });
    if (bestSlides.length < 2) return null;
    if (bestParent !== lockedContainer || bestSlides.length !== lockedSlides.length) {
      lockedContainer = bestParent;
      lockedSlides = bestSlides.slice();
      lockedTotal = bestSlides.length;
      attachAttrObserver(bestSlides);
    }
    return { container: bestParent, slides: bestSlides };
  }

  function detectCurrentSlide(slides) {
    // 1. Explicit active/current/selected markers (highest priority).
    for (var i = 0; i < slides.length; i++) {
      var el = slides[i];
      var cls = (el.className && typeof el.className === 'string') ? el.className : '';
      var aria = el.getAttribute && el.getAttribute('aria-current');
      var hidden = el.getAttribute && el.getAttribute('aria-hidden');
      var declaredIndex = parseInt((el.getAttribute && (el.getAttribute('data-slide-num') || el.getAttribute('data-slide-index'))) || '', 10);
      var resolvedIndex = Number.isFinite(declaredIndex) && declaredIndex >= 1 && declaredIndex <= slides.length
        ? declaredIndex
        : i + 1;
      if (/(^|\s)(active|current|selected|is-active|is-current|visible)(\s|$)/i.test(cls)) return resolvedIndex;
      if (aria && aria !== 'false') return resolvedIndex;
      if (hidden === 'false') return resolvedIndex;
    }
    // 2. Whichever slide's centre is nearest the viewport centre AND actually
    //    overlaps the viewport. Uses X+Y distance so horizontal carousels
    //    (translateX reels where every slide has top:0) resolve correctly —
    //    a Y-only check would always return slide 1 for those layouts.
    var vw = window.innerWidth;
    var vh = window.innerHeight;
    var vcx = vw / 2;
    var vcy = vh / 2;
    var bestIdx = 0;
    var bestScore = Infinity;
    for (var j = 0; j < slides.length; j++) {
      var el2 = slides[j];
      var css = window.getComputedStyle(el2);
      if (css.display === 'none' || css.visibility === 'hidden' || css.opacity === '0') continue;
      var r = el2.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue;
      // Must overlap the viewport to be considered "current".
      if (r.right <= 0 || r.left >= vw || r.bottom <= 0 || r.top >= vh) continue;
      var cx = r.left + r.width / 2;
      var cy = r.top + r.height / 2;
      var dx = Math.abs(cx - vcx);
      var dy = Math.abs(cy - vcy);
      var dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < bestScore) { bestScore = dist; bestIdx = j; }
    }
    return bestIdx + 1;
  }

  // Legacy fallback — only when the course has no structural slides.
  // Accepts DEDICATED counter elements only: the element's ENTIRE text must be
  // the counter, e.g. "1/27", "1 of 27", "Slide 2 of 27", "SLIDE 2 · 27".
  // Prose is never scanned: strings such as "30-60 days" or "2024–25" inside a
  // paragraph previously matched and hijacked the counter (locking the course
  // at "1 / 60" and forcing the learner back to page 1 on every navigation).
  // Hyphens/en-dashes are therefore only honoured with an explicit
  // "slide/page/step" prefix, never on a bare "N-M" range.
  var textCounterEl = null;
  var textLockedTotal = 0;
  function detectSlideProgressFromText() {
    var candidates = document.querySelectorAll('*');
    // Whole-text counters. Group 1 = current, group 2 = total.
    var strictCounter = /^(?:slide|page|step)?\\s*(\\d{1,4})\\s*(?:of|\\/|\\\\|·|\\u00b7)\\s*(\\d{1,4})$/i;
    var prefixedCounter = /^(?:slide|page|step)\\s*(\\d{1,4})\\s*(?:of|\\/|\\\\|·|\\u00b7|-|\\u2013|\\u2014)\\s*(\\d{1,4})$/i;
    var best = null;
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      // Only inspect leaf chrome elements — never containers holding prose.
      if (el.children.length > 0) continue;
      var text = (el.textContent || '').replace(/\\s+/g, ' ').trim();
      if (text.length === 0 || text.length > 24) continue;
      var match = text.match(strictCounter) || text.match(prefixedCounter);
      if (!match) continue;
      var current = parseInt(match[1], 10);
      var total = parseInt(match[2], 10);
      if (total < 2 || total > 500 || current < 1 || current > total) continue;
      if (textLockedTotal && total !== textLockedTotal) continue;
      // Stick to the element we locked onto first; otherwise prefer the largest.
      if (textCounterEl && el === textCounterEl) { best = { el: el, current: current, total: total }; break; }
      if (!best || total > best.total) best = { el: el, current: current, total: total };
    }
    if (!best) return null;
    textCounterEl = best.el;
    textLockedTotal = best.total;
    return { current: best.current, total: best.total };
  }


  function detectSlideProgress() {
    var set = findSlideSet();
    if (set) {
      return { current: detectCurrentSlide(set.slides), total: set.slides.length, structural: true };
    }
    var txt = detectSlideProgressFromText();
    if (txt) return { current: txt.current, total: txt.total, structural: false };
    return null;
  }

  function navigateToPage(pageNumber) {
    var n = parseInt(pageNumber, 10);
    if (!Number.isFinite(n) || n < 1) return false;
    var set = findSlideSet();
    var total = set ? set.slides.length : (lockedTotal || textLockedTotal || 0);
    if (total && n > total) n = total;
    var zeroBased = n - 1;

    var navFns = ['goToSlide', 'gotoSlide', 'showSlide', 'navigateToSlide', 'setSlide'];
    for (var i = 0; i < navFns.length; i++) {
      var fn = window[navFns[i]];
      if (typeof fn === 'function') {
        try {
          fn(zeroBased);
          setTimeout(checkProgress, 120);
          return true;
        } catch (err) {}
      }
    }

    if (window.Reveal && typeof window.Reveal.slide === 'function') {
      try {
        window.Reveal.slide(zeroBased);
        setTimeout(checkProgress, 120);
        return true;
      } catch (err) {}
    }

    if (set && set.slides[zeroBased]) {
      try {
        set.slides[zeroBased].scrollIntoView({ block: 'start' });
        setTimeout(checkProgress, 120);
        return true;
      } catch (err) {}
    }
    return false;
  }

  // Walk up from a candidate chrome element to its top/bottom bar container and hide it.
  // Lurneax owns the counter; any embedded "N / M" widget is a duplicate.
  // IMPORTANT: many courses put the "N / M" counter in the same bar as their own
  // Prev/Next buttons (e.g. Domain 8 uses <div class="botnav"> with prev/counter/next
  // as siblings). Hiding the whole bar would leave users no way to advance slides.
  // So if the surrounding bar also contains real navigation buttons, only hide
  // the counter element itself and leave the bar (and its buttons) intact.
  function barHasNavButtons(bar) {
    if (!bar || !bar.querySelectorAll) return false;
    var btns = bar.querySelectorAll('button, a, [role="button"]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      var idcls = ((b.id || '') + ' ' + (typeof b.className === 'string' ? b.className : '')).toLowerCase();
      var text = (b.textContent || '').trim().toLowerCase();
      var aria = (b.getAttribute && (b.getAttribute('aria-label') || '')).toLowerCase();
      if (/(^|[^a-z])(prev|previous|next|back|forward)([^a-z]|$)/.test(idcls) ||
          /^(prev|previous|next|back|forward|←|→|«|»)\b/.test(text) ||
          /(prev|previous|next|back|forward)/.test(aria) ||
          /[←→«»]/.test(text)) {
        return true;
      }
    }
    return false;
  }

  function hideBarAround(el) {
    if (!el) return;
    var node = el;
    var bar = null;
    for (var depth = 0; depth < 6 && node && node !== document.body; depth++) {
      var cs = window.getComputedStyle(node);
      var rect = node.getBoundingClientRect();
      var isBar = (cs.position === 'fixed' || cs.position === 'sticky') ||
                  (rect.width >= window.innerWidth * 0.6 && rect.height <= 120);
      if (isBar) { bar = node; }
      node = node.parentElement;
    }
    var target = bar || el;
    // If the bar we found also hosts Prev/Next controls, just hide the counter
    // leaf instead of nuking navigation.
    if (target !== el && barHasNavButtons(target)) {
      target = el;
    }
    if (target && target.style && target.style.display !== 'none') {
      target.setAttribute('data-lurneax-hidden', '1');
      target.style.display = 'none';
    }
  }

  function hideInnerSlideChrome() {
    // Always sweep for any leaf element rendering an "N / M" counter and hide
    // its surrounding bar — even in structural mode, so baked-in course
    // footers (e.g. "31 / 31") never appear alongside the Lurneax counter.
    var strict = /^\\s*\\d{1,4}\\s*[\\/]\\s*\\d{1,4}\\s*$/;
    var candidates = document.querySelectorAll('*');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      if (el.children.length > 0) continue;
      var text = (el.textContent || '').trim();
      if (text.length === 0 || text.length > 15) continue;
      if (strict.test(text)) hideBarAround(el);
    }
  }


  var isSlideMode = false;
  var maxAllowed = 1; // Max page user is allowed to navigate to

  // Listen for navigation permission updates from parent
  window.addEventListener('message', function(e) {
    if (e.data && e.data.type === 'lurneax-max-allowed') {
      maxAllowed = e.data.maxAllowed;
    }
    if (e.data && e.data.type === 'lurneax-resume-page') {
      var resumePage = parseInt(e.data.page, 10);
      if (resumePage > 1 && resumePage <= maxAllowed) {
        setTimeout(function() { navigateToPage(resumePage); }, 100);
        setTimeout(function() { navigateToPage(resumePage); }, 500);
      }
    }
  });

  function checkProgress() {
    var slideInfo = detectSlideProgress();
    if (slideInfo) {
      isSlideMode = true;
      dbg('detected slide', slideInfo, 'maxAllowed=', maxAllowed);
      sendPageInfo(slideInfo.current, slideInfo.total);
      hideInnerSlideChrome();

      // If user tries to jump ahead beyond allowed, force back.
      if (slideInfo.current > maxAllowed) {
        dbg('current>maxAllowed, forcing back');
        var prevBtn = document.querySelector('[class*="prev"]:not([class*="preview"]), [aria-label*="prev" i], [aria-label*="Previous" i]');
        if (prevBtn && typeof prevBtn.click === 'function') prevBtn.click();
        return;
      }

      var progress = Math.min(100, Math.round((slideInfo.current / slideInfo.total) * 100));
      sendProgress(progress);
      return;
    }

    if (!isSlideMode) {
      var scrollProg = getScrollProgress();
      if (scrollProg >= 0) sendProgress(scrollProg);
    }
  }

  var userInteracted = false;
  function markInteraction() {
    if (userInteracted) return;
    userInteracted = true;
    try { window.parent.postMessage({ type: 'lurneax-user-interaction' }, '*'); } catch (e) {}
  }

  window.addEventListener('scroll', function() { markInteraction(); checkProgress(); }, { passive: true });
  window.addEventListener('resize', checkProgress);
  window.addEventListener('click', function() { markInteraction(); setTimeout(checkProgress, 200); setTimeout(checkProgress, 600); });
  window.addEventListener('keydown', function() { markInteraction(); setTimeout(checkProgress, 200); setTimeout(checkProgress, 600); });
  window.addEventListener('touchstart', markInteraction, { passive: true });
  window.addEventListener('wheel', markInteraction, { passive: true });
  window.addEventListener('pointerdown', markInteraction, { passive: true });
  window.addEventListener('hashchange', function() { setTimeout(checkProgress, 200); });
  window.addEventListener('popstate', function() { setTimeout(checkProgress, 200); });

  var debounceTimer;
  var observer = new MutationObserver(function() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(checkProgress, 150);
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true });

  setTimeout(checkProgress, 300);
  setTimeout(checkProgress, 1000);
  setTimeout(checkProgress, 2000);
  setInterval(checkProgress, 2000);

  // --- Hide in-HTML complete/finish buttons ---
  function hideCompletionButtons() {
    var buttons = document.querySelectorAll('button, a.btn, a.button, [class*="btn"], [role="button"]');
    for (var i = 0; i < buttons.length; i++) {
      var text = (buttons[i].textContent || '').trim().toLowerCase();
      var idOrClass = ((buttons[i].id || '') + ' ' + (buttons[i].className || '')).toLowerCase();
      if (text === 'complete' || text === 'completed' || text === 'complete course' || text === 'finish' ||
          text === 'finish course' || text === 'done' || text === '✓ done' || text === '✔ done' ||
          text === 'mark complete' || text === 'submit completion' || /finish\s*&\s*submit/.test(text) ||
          /mark\s+.*complete/.test(text) || text === '✓ complete' || text === '✔ complete' ||
          text === '✓ completed' || text === '✔ completed' || /(^|[^a-z])(finish|complete|done)([^a-z]|$)/.test(idOrClass)) {
        buttons[i].style.display = 'none';
        var sibling = buttons[i].nextElementSibling;
        if (sibling) {
          var siblingText = (sibling.textContent || '').trim().toLowerCase();
          if (/click\s+to\s+mark|trigger\s+your\s+certificate|submit\s+completion/.test(siblingText)) {
            sibling.style.display = 'none';
          }
        }
      }
    }
  }
  
  // Track whether all answers/quizzes inside the HTML have been submitted
  var submitDetected = false;
  function getDirectButtonText(el) {
    var directText = '';
    for (var i = 0; i < el.childNodes.length; i++) {
      if (el.childNodes[i].nodeType === Node.TEXT_NODE) directText += ' ' + el.childNodes[i].textContent;
    }
    return (directText || el.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function isRealAnswerSubmitButton(el) {
    if (el.tagName === 'INPUT') {
      var inputType = (el.getAttribute('type') || '').toLowerCase();
      var inputValue = (el.getAttribute('value') || '').replace(/\s+/g, ' ').trim().toLowerCase();
      return inputType === 'submit' && /\b(submit|check answer|check answers)\b/.test(inputValue);
    }

    var text = getDirectButtonText(el);
    if (!text || text.length > 60) return false;
    return /^(submit|submit answer|submit answers|check answer|check answers)$/i.test(text);
  }

  function trackSubmissions() {
    var submitBtns = document.querySelectorAll('button, input[type="submit"], [class*="btn"], [role="button"]');
    var hasSubmit = false;
    for (var i = 0; i < submitBtns.length; i++) {
      if (isRealAnswerSubmitButton(submitBtns[i])) {
        hasSubmit = true;
        submitBtns[i].addEventListener('click', function() {
          setTimeout(function() {
            window.parent.postMessage({ type: 'lurneax-answer-submitted' }, '*');
          }, 500);
        }, { once: false });
      }
    }
    if (hasSubmit && !submitDetected) {
      submitDetected = true;
      window.parent.postMessage({ type: 'lurneax-has-submit-buttons' }, '*');
    }
  }

  hideCompletionButtons();
  trackSubmissions();
  
  var hideObserver = new MutationObserver(function() {
    hideCompletionButtons();
    trackSubmissions();
  });
  hideObserver.observe(document.body, { childList: true, subtree: true });
})();
</script>`;

    // Only inject color overrides if HTML is unstyled.
    // If the HTML ships its own <style> block or a --brand token, it's self-themed —
    // forcing Lurneax colors over the top breaks its design (e.g. [class*="active"]
    // selectors painting coral slabs over the author's slides/sections).
    const hasLurneaxColors = rawHtml.includes('#FDF0E8') || rawHtml.includes('#F44321') || rawHtml.includes('--brand:#F44321');
    const isSelfThemed = /<style[\s>]/i.test(rawHtml) || /--brand\s*:/i.test(rawHtml);
    let result = rawHtml;

    if (!hasLurneaxColors && !isSelfThemed) {
      if (result.includes("</head>")) {
        result = result.replace("</head>", colorOverrides + "</head>");
      } else if (result.includes("<body")) {
        result = result.replace("<body", colorOverrides + "<body");
      } else {
        result = colorOverrides + result;
      }
    }

    if (result.includes("</body>")) {
      result = result.replace("</body>", trackingScript + "</body>");
    } else {
      result = result + trackingScript;
    }

    return result;
  }, [rawHtml]);

  // The resume-page target is captured once at mount from persisted state.
  // We must NOT re-derive it from live `currentPage`, otherwise every
  // detected-slide update would re-post resume-page → the iframe would
  // scrollIntoView → scroll event → new detection → post again (auto-scroll loop).
  const resumePageRef = useRef<number>(
    !isAlreadyCompleted && seedCurrent > 1 ? seedCurrent : 0
  );
  const resumeSentRef = useRef(false);

  const postMaxAllowed = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      { type: "lurneax-max-allowed", maxAllowed: maxPageReached + 1 },
      "*"
    );
  }, [maxPageReached]);

  // Send max allowed whenever it changes (safe — iframe just updates a variable)
  useEffect(() => {
    postMaxAllowed();
  }, [postMaxAllowed]);

  // Listen for progress & page info from iframe
  const debugRef = useRef<boolean>(typeof window !== 'undefined' && /[?&]htmlDebug=1\b/.test(window.location.search));
  const dbg = useCallback((...args: unknown[]) => {
    if (debugRef.current) console.log('[HTMLPlayer/react]', ...args);
  }, []);
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "lurneax-scroll-progress") {
        const prog = event.data.progress as number;
        setScrollProgress(prog);
      }

      if (event.data?.type === "lurneax-page-info") {
        const current = event.data.current as number;
        const total = event.data.total as number;

        setIsSlideMode(true);
        setTotalPages(total);

        setMaxPageReached((prevMax) => {
          const gated = current <= prevMax + 1;
          const nextMax = gated ? Math.max(prevMax, current) : prevMax;
          dbg('page-info received', { current, total, prevMax, gated, nextMax });
          setCurrentPage(gated ? current : Math.max(1, prevMax));
          setVisitedPages((prev) => {
            if (!gated) return prev;
            const next = new Set(prev);
            next.add(current);
            return next;
          });
          return nextMax;
        });
      }


      // Track when submit buttons inside HTML are detected
      if (event.data?.type === "lurneax-has-submit-buttons") {
        setHasSubmitButtons(true);
      }

      // Track when answers are submitted inside HTML
      if (event.data?.type === "lurneax-answer-submitted") {
        setAnswersSubmitted(true);
      }
      // First real user interaction inside the iframe — unlocks progress.
      if (event.data?.type === "lurneax-user-interaction") {
        setHasInteracted(true);
      }

      // Terminal completion signal used by HR/WHS SlideDeck template:
      //   window.parent.postMessage({ action: 'complete', ... }, '*')
      // Treat as "user reached the end" — mark every slide visited so the
      // Lurneax Complete button unlocks, then leave the actual completion
      // click to the user (never auto-complete).
      if (event.data?.action === "complete") {
        setHasInteracted(true);
        setIsSlideMode(true);
        setTotalPages((prevTotal) => {
          const total = prevTotal > 0 ? prevTotal : (typeof event.data.totalSlides === "number" ? event.data.totalSlides : 0);
          if (total > 0) {
            const all = new Set<number>();
            for (let i = 1; i <= total; i++) all.add(i);
            setVisitedPages(all);
            setMaxPageReached(total);
            setCurrentPage(total);
          }
          return total;
        });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // On iframe load: send max-allowed, and send resume-page ONCE (never again).
  const handleIframeLoad = useCallback(() => {
    postMaxAllowed();
    window.setTimeout(postMaxAllowed, 500);
    if (!resumeSentRef.current && resumePageRef.current > 1) {
      const iframe = iframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          { type: "lurneax-resume-page", page: resumePageRef.current },
          "*"
        );
        resumeSentRef.current = true;
      }
    }
  }, [postMaxAllowed]);

  const pagesReady = allPagesVisited;
  // In slide mode, visiting every slide is sufficient to complete — submit-button
  // gating only applies to non-slide (scroll) HTML courses that embed real quizzes.
  const submitsReady = isSlideMode || !hasSubmitButtons || answersSubmitted;
  const canComplete = pagesReady && submitsReady;

  // Debounced progress persistence
  useEffect(() => {
    if (!onProgressChange || isAlreadyCompleted) return;
    const timer = setTimeout(() => {
      const percent = isSlideMode && totalPages > 0
        ? Math.min(99, Math.round((visitedPages.size / totalPages) * 100))
        : Math.min(99, scrollProgress);
      const payload = {
        visitedPages: Array.from(visitedPages).sort((a, b) => a - b),
        currentPage,
        totalPages,
        maxPageReached: maxPageReached === Infinity ? 0 : maxPageReached,
        scrollProgress,
        answersSubmitted,
        progressPercent: percent,
      };
      dbg('onProgressChange emitting', payload);
      onProgressChange(payload);
    }, 1200);
    return () => clearTimeout(timer);
  }, [visitedPages, currentPage, totalPages, maxPageReached, scrollProgress, answersSubmitted, isSlideMode, isAlreadyCompleted, onProgressChange]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 px-4 py-2 border-b bg-card shadow-sm shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setExitDialogOpen(true)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-sm font-semibold truncate">{courseTitle}</h1>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          {/* Progress indicator */}
          <div className="hidden sm:flex items-center gap-2 w-40">
            <Progress value={progress} className="h-2" />
            <span className="text-xs text-muted-foreground w-8 text-right">
              {progress}%
            </span>
          </div>

          {/* Page counter for slide mode */}
          {isSlideMode && totalPages > 0 && (
            <span className="text-xs font-medium text-muted-foreground">
              {currentPage} / {totalPages}
            </span>
          )}

          {/* Complete button or badge */}
          {isAlreadyCompleted ? (
            <Button disabled size="sm" variant="secondary" className="gap-1">
              <CheckCircle2 className="h-4 w-4 text-green-600" />
              Completed
            </Button>
          ) : canComplete ? (
            <Button
              size="sm"
              disabled={isCompleting}
              onClick={onComplete}
              className="gap-1 bg-gradient-to-r from-primary to-primary/80 animate-in fade-in"
            >
              {isCompleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trophy className="h-4 w-4" />
              )}
              {isCompleting ? "Completing..." : "Complete Course"}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Mobile progress bar */}
      <div className="sm:hidden">
        <Progress value={progress} className="h-1 rounded-none" />
      </div>

      {/* Full-screen iframe */}
      <iframe
        ref={iframeRef}
        srcDoc={enhancedHtml()}
        className="flex-1 w-full border-0"
        sandbox="allow-scripts allow-same-origin"
        title={courseTitle}
        onLoad={handleIframeLoad}
      />

      {/* Post-completion success overlay */}
      {justCompleted && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/85 backdrop-blur-sm animate-in fade-in">
          <div className="max-w-md w-[90%] rounded-2xl border bg-card shadow-2xl p-8 text-center space-y-5">
            <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
              <Trophy className="h-8 w-8 text-primary-foreground" />
            </div>
            <div className="space-y-1">
              <h2 className="text-2xl font-bold">Course Complete</h2>
              <p className="text-sm text-muted-foreground">
                Congratulations – you've finished <span className="font-medium text-foreground">{courseTitle}</span>. Your certificate has been issued.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <Button
                className="flex-1 gap-2"
                onClick={() => (onViewCertificate ? onViewCertificate() : navigate(exitPath))}
              >
                <Award className="h-4 w-4" />
                View Certificate
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/learning/my-progress")}
              >
                My Progress
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={exitDialogOpen} onOpenChange={setExitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End course midway?</AlertDialogTitle>
            <AlertDialogDescription>
              Any unsaved progress on the current page may be lost. Do you still want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate(exitPath)}>Yes, exit</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
